// src/components/DiscountModal.jsx
import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Button,
  TextField,
  Typography,
} from "@mui/material";
import axios from "axios";
import Swal from "sweetalert2";
import { saveAs } from "file-saver";
import ExcelJS from "exceljs";
import { getAuthCredentials } from "../../../utils/auth";

const DiscountModal = ({ open, onClose, onReopen }) => {
  const [discountType, setDiscountType] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [loading, setLoading] = useState(false);
  const { token } = getAuthCredentials();

  /* ------------------------------------------------------------------ */
  const exportToExcel = async () => {
    if (!startDate || !endDate) {
      alert("Start date and end date are required.");
      return;
    }

    setLoading(true);
    try {
      /* ------------------------------ fetch */
      const { data } = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/v1/api/reports/discount-summary-data`,
        { startDate, endDate, discountType },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const { header, columns, rows, totals } = data;

      /* ------------------------------ wb / ws */
      const wsName =
        {
          PWD: "PWD Report",
          Senior: "Senior Citizen Report",
          SoloParent: "Solo Parent Report",
          Diplomat: "Diplomat Report",
          Athlete: "Athlete Report",
        }[discountType] ||
        (discountType === "Voucher" ? "Voucher Report" : "Discount Report");

      const wb = new ExcelJS.Workbook();
      const ws = wb.addWorksheet(wsName);

      /* ------------------------------ helpers */
      const hdrAlign = { horizontal: "center", vertical: "middle" };
      const thinBorder = {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "thin" },
        right: { style: "thin" },
      };
      const merge = (ref) => ws.mergeCells(ref);

      /* ------------------------------ store header */
      merge("A1:L1");
      ws.getCell("A1").value = header.storeName;
      ws.getCell("A1").alignment = hdrAlign;
      ws.getCell("A1").font = { bold: true, size: 14 };

      merge("A2:L2");
      ws.getCell("A2").value = header.address;
      merge("A3:L3");
      ws.getCell("A3").value = `TIN: ${header.TIN}`;
      merge("A4:L4");
      ws.getCell("A4").value = `Period: ${header.period.startDate} - ${header.period.endDate}`;
      merge("A5:L5");
      ws.getCell(
        "A5"
      ).value = `Sparkle POS v0.1.0  |  ${new Date().toLocaleString("en-PH", {
        timeZone: "Asia/Manila",
      })}`;

      merge("A6:L6");
      ws.getCell("A6").value = `User: ${header.user?.name || "N/A"} (${
        header.user?.id || "N/A"
      })`;

      /* ------------------------------ title */
      merge("A8:L8");
      ws.getCell("A8").value = header.reportTitle;
      ws.getCell("A8").alignment = hdrAlign;
      ws.getCell("A8").font = { bold: true, size: 16 };

      /* ------------------------------ column prep */
      let baseCols = [...columns];
      if (["PWD", "Senior"].includes(discountType))
        baseCols = columns.slice(0, 8);
      else if (discountType === "SoloParent") baseCols = columns.slice(0, 8);

      const extraCount = ["PWD", "Senior"].includes(discountType)
        ? 3
        : discountType === "SoloParent"
        ? 2
        : 0;

      ws.columns = [
        ...baseCols.map((c) => ({
          key  : c.header.replace(/\s+/g, "_"),
          width: Math.max(20, c.header.length + 2),
        })),
        ...Array(extraCount).fill({ key: "", width: 15 }),
      ];

      /* ------------------------------ vertical merges for base headers */
      baseCols.forEach((c, i) => {
        const L = String.fromCharCode(65 + i);
        merge(`${L}10:${L}11`);
        const cell = ws.getCell(`${L}10`);
        cell.value = c.header;
        cell.alignment = hdrAlign;
        cell.font = { bold: true };
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: c.color },
        };
        cell.border = thinBorder;
      });

      /* ------------------------------ specialised band WITH COLOUR  */
      if (["Senior", "PWD"].includes(discountType)) {
        /* Discount band */
        merge("I10:J10");
        ws.getCell("I10").value = "Discount";
        ws.getCell("I10").alignment = hdrAlign;
        ws.getCell("I10").font = { bold: true };
        ws.getCell("I10").fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FFD700" }, // yellow
        };
        ws.getCell("I10").border = thinBorder;

        ws.getCell("I11").value = "5%";
        ws.getCell("J11").value = "20%";
        ["I11", "J11"].forEach((ref) => {
          ws.getCell(ref).alignment = hdrAlign;
          ws.getCell(ref).fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "FFD700" }, // yellow
          };
          ws.getCell(ref).border = thinBorder;
        });

        /* Net Sales header */
        merge("K10:K11");
        ws.getCell("K10").value = "Net Sales";
        ws.getCell("K10").alignment = hdrAlign;
        ws.getCell("K10").font = { bold: true };
        ws.getCell("K10").fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "808080" }, // dark-gray
        };
        ws.getCell("K10").border = thinBorder;
      } else if (discountType === "SoloParent") {
        merge("I10:I11");
        ws.getCell("I10").value = "Discount";
        ws.getCell("I10").alignment = hdrAlign;
        ws.getCell("I10").fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FFD700" },
        };
        ws.getCell("I10").border = thinBorder;

        merge("J10:J11");
        ws.getCell("J10").value = "Net Sales";
        ws.getCell("J10").alignment = hdrAlign;
        ws.getCell("J10").fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "808080" },
        };
        ws.getCell("J10").border = thinBorder;
      } 

      /* ------------------------------ data rows */
      rows.forEach((r) => {
        const vals = baseCols.map((c) => r[c.header] || "");
        if (["Senior", "PWD"].includes(discountType)) {
          vals.push(""); // 5 %
          vals.push(r["Discount 20%"] || ""); // 20 %
          vals.push(r["Net Sales"] || "");
        } else if (discountType === "SoloParent") {
          vals.push(r["Discount"] || "");
          vals.push(r["Net Sales"] || "");
        }
        ws.addRow(vals);
      });

      /* ------------------------------ totals */
      {
        const tRow = ws.addRow([]);
        tRow.getCell(1).value = "GRAND TOTAL:";
        tRow.font = { bold: true };

        /* 1️⃣  figure-out which column = Gross / Discount / Net */
        const baseLen = baseCols.length; // 8 for PWD/Senior & Solo
        let grossCol = -1;
        let discCol = -1;
        let netCol = -1;

        // scan the base headers first
        baseCols.forEach((c, i) => {
          const h = c.header.toLowerCase();
          if (h.includes("gross") || h.includes("sales (inclusive"))
            grossCol = i + 1;
          if (
            (h.includes("discount") || h.includes("voucher")) &&
            discCol === -1
          )
            discCol = i + 1;
          if (h.includes("net")) netCol = i + 1;
        });

        // then patch-in the special layouts
        if (["Senior", "PWD"].includes(discountType)) {
          // … [base 8]  |  9 = 5% blank  | 10 = Discount 20% | 11 = Net
          discCol = baseLen + 2; // col 10
          netCol = baseLen + 3; // col 11
        } else if (discountType === "SoloParent") {
          // … [base 8]  |  9 = Discount  | 10 = Net
          discCol = baseLen + 1; // col 9
          netCol = baseLen + 2; // col 10
        } else if (discountType === "Voucher") {
          grossCol = baseCols.findIndex(c =>
                      c.header.toLowerCase().includes("gross")) + 1;
          discCol  = baseCols.findIndex(c =>
                      c.header.toLowerCase().includes("voucher amount")) + 1;
          netCol   = baseCols.findIndex(c =>
                      c.header.toLowerCase().includes("net")) + 1;
        }

        /* 2️⃣  write the numbers (leave cell empty if a column isn't present) */
        if (grossCol > 0)
          tRow.getCell(grossCol).value = +totals.grandGrossSales;
        if (discCol > 0)
          tRow.getCell(discCol).value = +totals.grandDiscountAmount;
        if (netCol > 0) tRow.getCell(netCol).value = +totals.grandNetSales;

        // optionally give those cells bold font / border
        [grossCol, discCol, netCol].forEach((col) => {
          if (col > 0) {
            const cell = tRow.getCell(col);
            cell.font = { bold: true };
            cell.border = thinBorder;
          }
        });
      }

      /* ------------------------------ export */
      const buf = await wb.xlsx.writeBuffer();
      saveAs(
        new Blob([buf], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        }),
        `${wsName.replace(/\s+/g, "_")}.xlsx`
      );
    } catch (err) {
      console.error("Error generating Excel report:", err);
      const is404 = err.response?.status === 404;
      Swal.fire({
        icon: is404 ? "info" : "error",
        title: is404 ? "No Data Found" : "Error",
        text: is404
          ? err.response.data.message
          : "Failed to generate the Discount Summary Report. Please try again.",
      }).then(() => {
        if (onReopen) onReopen();
      });
    } finally {
      setLoading(false);
    }
  };
  /* ------------------------------------------------------------------ */

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Generate Discount Summary Report</DialogTitle>

      <DialogContent>
        <Typography sx={{ mb: 2 }}>
          Please select the discount type and the date range for the report:
        </Typography>

        <TextField
          select
          fullWidth
          label="Discount Type"
          sx={{ mb: 2 }}
          value={discountType}
          onChange={(e) => setDiscountType(e.target.value)}
        >
          <MenuItem value="">Select Discount</MenuItem>
          <MenuItem value="PWD">PWD</MenuItem>
          <MenuItem value="Senior">Senior</MenuItem>
          <MenuItem value="SoloParent">Solo Parent</MenuItem>
          <MenuItem value="Diplomat">Diplomat</MenuItem>
          <MenuItem value="Athlete">Athlete</MenuItem>
          <MenuItem value="Voucher">Voucher</MenuItem>
        </TextField>

        <TextField
          fullWidth
          type="date"
          label="Start Date"
          sx={{ mb: 2 }}
          InputLabelProps={{ shrink: true }}
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          inputProps={{
            min: "2025-05-17",
            max: new Date().toISOString().split("T")[0],
          }}
        />

        <TextField
          fullWidth
          type="date"
          label="End Date"
          InputLabelProps={{ shrink: true }}
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
          inputProps={{
            min: startDate || "2025-05-17",
            max: new Date().toISOString().split("T")[0],
          }}
        />
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={exportToExcel}
          disabled={loading || !discountType || !startDate || !endDate}
        >
          {loading ? "Generating…" : "Generate"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DiscountModal;
